<template>
  <div class="search-results-list">
    <ul role="listbox" data-test="search-results-top">
      <!-- eslint-disable-next-line vuejs-accessibility/role-has-required-aria-props -->
      <li
        v-for="(result, index) in topResults"
        :key="result.item.name + index"
        :data-test="'search-result-options-' + index"
        role="option"
        :aria-label="`Search for ${result.item.name}`"
        :class="[
          'search-result-container',
          { 'is-active': hoveredSearchResultId === result.item.id },
        ]"
        tabIndex="0"
        @focus="handleFocus(result.item.id)"
        @blur="handleBlur(result.item.id)"
        @click="onSearchResultSelected(result, index + 1)"
        @keyup.enter="onSearchResultSelected(result, index + 1)"
        @mouseenter="handleMouseEnter(result.item.id)"
        @mouseleave="handleMouseLeave(result.item.id)"
      >
        <component
          :is="resultItemType(result.item.onxType ?? undefined)"
          :result="result"
        />
      </li>
    </ul>

    <button
      v-if="bottomResults.length > 0 && !isBottomVisible"
      data-test="search-results-show-more-button"
      class="show-more-button"
      @click.stop="isBottomVisible = true"
    >
      Show More
    </button>

    <ul v-if="isBottomVisible" role="listbox" data-test="search-results-bottom">
      <!-- eslint-disable-next-line vuejs-accessibility/role-has-required-aria-props -->
      <li
        v-for="(result, index) in bottomResults"
        :key="result.item.name + index"
        :data-test="'search-result-options-' + index"
        role="option"
        :aria-label="`Search for ${result.item.name}`"
        tabIndex="0"
        :class="[
          'search-result-container',
          { 'is-active': hoveredSearchResultId === result.item.id },
        ]"
        @click="onSearchResultSelected(result, index + 1 + numberToShow)"
        @keyup.enter="onSearchResultSelected(result, index + 1 + numberToShow)"
      >
        <component
          :is="resultItemType(result.item.onxType ?? undefined)"
          :result="result"
        />
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, watch, onUnmounted } from 'vue'
import genericResultItem from '@/components/search/result-items/generic-result-item.vue'
import fishingAreaItem from '@/components/search/result-items/fishing-area-item.vue'
import waterAccessItem from '@/components/search/result-items/water-access-item.vue'
import regionItem from '@/components/search/result-items/region-item.vue'

import pubsub from '@/lib/pubsub'
import { KgType } from '@/lib/knowledge-graph/types'
import type { SearchResultItem } from '@onxmaps/search-web'
import {
  featuresHovered,
  setFeaturesHovered,
  featuresInView,
} from '@/services/map-service'
import { isSearchPinsV2Enabled } from '@/services/search-service'
import { SEARCH_PIN_SOURCE } from '@/lib/enums'

const isBottomVisible = ref<boolean>(false)
const hoveredSearchResultId = ref<string | null>(null)

interface ResultsListProps {
  searchResults: SearchResultItem[]
  showAll?: boolean
  numberToShow?: number
}
const {
  searchResults,
  showAll = true,
  numberToShow = 3,
} = defineProps<ResultsListProps>()

const emit = defineEmits(['resultSelected'])

onMounted(() => {
  pubsub.subscribe('highlight-search-map-marker', (id: string) => {
    hoveredSearchResultId.value = id
  })
  pubsub.subscribe('unhighlight-search-map-marker', () => {
    hoveredSearchResultId.value = null
  })
})

const searchPinsV2Enabled = computed(() => isSearchPinsV2Enabled())

const topResults = computed(() => {
  if (showAll) {
    return searchResults
  }

  return searchResults.slice(0, numberToShow)
})

const bottomResults = computed(() => {
  if (showAll) {
    return []
  }

  return searchResults.slice(numberToShow)
})

const handleFocus = (id: string) => {
  pubsub.publish('highlight-search-map-marker', id)
}
const handleBlur = (id: string) => {
  pubsub.publish('unhighlight-search-map-marker', id)
}
const handleMouseEnter = (id: string) => {
  hoveredSearchResultId.value = id
  pubsub.publish('highlight-search-map-marker', id)

  if (searchPinsV2Enabled.value) {
    const currFeatures = featuresInView()
    const currResultFeatures = currFeatures?.[SEARCH_PIN_SOURCE]
    const feature = currResultFeatures?.find((f) => f.id === id)

    if (feature) {
      setFeaturesHovered([feature])
    }
  }
}
const handleMouseLeave = (id: string) => {
  pubsub.publish('unhighlight-search-map-marker', id)

  if (searchPinsV2Enabled.value) {
    setFeaturesHovered([])
  }
}
const onSearchResultSelected = (result: SearchResultItem, resultRank = 0) => {
  const resultWithRank = { ...result, search_rank: resultRank }
  emit('resultSelected', resultWithRank)
}
const resultItemType = (onxType: string | undefined) => {
  const itemTypes: Record<string, any> = {
    [KgType.FISHING_AREA]: fishingAreaItem,
    [KgType.WATER_ACCESS]: waterAccessItem,
    [KgType.FISHING_REGION]: regionItem,
  }
  return onxType ? itemTypes[onxType] || genericResultItem : genericResultItem
}

const unwatchHoveredFeatures = watch(
  () => featuresHovered(),
  (newVal) => {
    if (searchPinsV2Enabled.value) {
      // Watch features hovered, and if the top feature is a search result, emit the id to highlight in list
      const searchPinFeature = newVal?.find(
        (feat) => feat.source === SEARCH_PIN_SOURCE
      )
      if (searchPinFeature) {
        hoveredSearchResultId.value = searchPinFeature.id as string
      } else {
        hoveredSearchResultId.value = null
      }
    }
  }
)

onUnmounted(() => {
  unwatchHoveredFeatures()
})

// Exposed for testing
defineExpose({
  hoveredSearchResultId,
})
</script>

<style lang="scss" scoped>
.search-result-container {
  &.is-active,
  &:hover,
  &:focus {
    background-color: var(--ys-color-background-secondary);
    outline: none;
  }
}

.search-results-list {
  margin-bottom: 20px;

  ul li {
    border-bottom: 1px solid var(--ys-color-divider-base);
  }
}

.show-more-button {
  background: none;
  color: var(--ys-color-text-primary);
  width: 100%;
}
</style>
